/**
 * Address validation utilities for different blockchain networks
 */

// Ethereum address validation (also works for EVM compatible chains)
export function isValidEthereumAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  // Basic format check: 0x followed by 40 hexadecimal characters
  const ethAddressRegex = /^0x[a-fA-F0-9]{40}$/;
  return ethAddressRegex.test(address);
}

// Solana address validation  
export function isValidSolanaAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  // Solana addresses are 32-44 characters, base58 encoded
  const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
  
  if (!solanaAddressRegex.test(address)) return false;
  
  // Additional checks to avoid common mistakes
  if (address.includes('0') || address.includes('O') || address.includes('I') || address.includes('l')) {
    return false; // Base58 doesn't use these characters
  }
  
  return true;
}

// Bitcoin address validation (basic)
export function isValidBitcoinAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  // Legacy P2PKH (starts with 1)
  const legacyRegex = /^[1][a-km-zA-HJ-NP-Z1-9]{25,34}$/;
  
  // P2SH (starts with 3)  
  const p2shRegex = /^[3][a-km-zA-HJ-NP-Z1-9]{25,34}$/;
  
  // Bech32 (starts with bc1)
  const bech32Regex = /^bc1[a-z0-9]{39,59}$/;
  
  return legacyRegex.test(address) || p2shRegex.test(address) || bech32Regex.test(address);
}

// Tron address validation
export function isValidTronAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  // Tron addresses start with 'T' and are 34 characters long
  const tronAddressRegex = /^T[A-Za-z1-9]{33}$/;
  return tronAddressRegex.test(address);
}

// Polkadot address validation (simplified)
export function isValidPolkadotAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  // Polkadot addresses start with '1' and are base58 encoded
  const polkadotAddressRegex = /^1[a-km-zA-HJ-NP-Z1-9]{47}$/;
  return polkadotAddressRegex.test(address);
}

// Main validation function
export function validateAddress(address: string, network: string): {
  isValid: boolean;
  error?: string;
} {
  if (!address || !address.trim()) {
    return { isValid: false, error: 'Address is required' };
  }
  
  const trimmedAddress = address.trim();
  
  switch (network) {
    case 'Ethereum':
    case 'Arbitrum':
    case 'Base':
    case 'Polygon':
    case 'BNB':
    case 'Avalanche':
      if (!isValidEthereumAddress(trimmedAddress)) {
        return { 
          isValid: false, 
          error: `Invalid ${network} address format. Must be a valid Ethereum address (0x...)` 
        };
      }
      break;
      
    // Non-EVM network support removed - EVM chains only
      
    default:
      return { isValid: false, error: `Unsupported network: ${network}` };
  }
  
  return { isValid: true };
}

// Validate network selection against whitelist - EVM chains only
export function validateNetwork(network: string): {
  isValid: boolean;
  error?: string;
} {
  const allowedNetworks = [
    'Ethereum', 'Arbitrum', 'Base', 'Polygon', 'BNB', 'Avalanche'
  ];
  
  if (!network || !allowedNetworks.includes(network)) {
    return { 
      isValid: false, 
      error: `Unsupported network: ${network}. Must be one of: ${allowedNetworks.join(', ')}` 
    };
  }
  
  return { isValid: true };
}

// Validate token selection against whitelist
export function validateToken(token: string, network: string): {
  isValid: boolean;
  error?: string;
} {
  if (!token) {
    return { isValid: true }; // Native token is always allowed
  }
  
  const allowedTokens: Record<string, string[]> = {
    'Ethereum': ['USDC', 'USDT', 'DAI', 'WETH', 'WBTC'],
    'Arbitrum': ['USDC', 'USDT', 'DAI', 'WETH', 'WBTC'],
    'Base': ['USDC', 'USDT', 'DAI', 'WETH', 'WBTC'],
    'Polygon': ['USDC', 'USDT', 'DAI', 'WETH', 'WBTC'],
    'BNB': ['USDC', 'USDT', 'DAI', 'WETH', 'WBTC'],
    'Avalanche': ['USDC', 'USDT', 'DAI', 'WETH', 'WBTC']
  };
  
  const tokensForNetwork = allowedTokens[network] || [];
  
  if (!tokensForNetwork.includes(token)) {
    return { 
      isValid: false, 
      error: `Unsupported token ${token} for ${network} network` 
    };
  }
  
  return { isValid: true };
}

// Sanitize input to prevent XSS
export function sanitizeInput(input: string): string {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>'"&]/g, '') // Remove potentially dangerous characters
    .slice(0, 100); // Limit length
}

// Validate amount input
export function validateAmount(amount: string, min = 0.01, max = 100000): {
  isValid: boolean;
  error?: string;
} {
  if (!amount || !amount.trim()) {
    return { isValid: false, error: 'Amount is required' };
  }
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return { isValid: false, error: 'Amount must be a valid number' };
  }
  
  if (numAmount < min) {
    return { isValid: false, error: `Amount must be at least $${min}` };
  }
  
  if (numAmount > max) {
    return { isValid: false, error: `Amount cannot exceed $${max}` };
  }
  
  // Check for reasonable decimal places (max 6)
  const decimalPlaces = (amount.split('.')[1] || '').length;
  if (decimalPlaces > 6) {
    return { isValid: false, error: 'Amount cannot have more than 6 decimal places' };
  }
  
  return { isValid: true };
}